/**
 * Domaines 3, 4, 5 et 6 du MLD v5.0 — RBAC, journalisation, sécurité
 * documentaire, notification in-app.
 * Source : `db/migrations/001_init_schema.sql`.
 */
import { valeurs } from './enumeration.js';
// -----------------------------------------------------------------------------
// RBAC
// -----------------------------------------------------------------------------
/** `UTILISATEUR.statut_compte`. */
export const STATUT_COMPTE = {
    ACTIF: 'actif',
    VERROUILLE: 'verrouille',
    DESACTIVE: 'desactive',
};
export const STATUTS_COMPTE = valeurs(STATUT_COMPTE);
/** `ACTEUR.type_acteur` — nature du rattachement d'un compte au métier. */
export const TYPE_ACTEUR = {
    PROFESSIONNEL: 'professionnel',
    AGENT_ADMINISTRATION: 'agent_administration',
    ADMIN: 'admin',
    CNSP: 'cnsp',
};
export const TYPES_ACTEUR = valeurs(TYPE_ACTEUR);
/** `ROLE.libelle_role` — les 5 rôles pré-chargés par les seeds. */
export const LIBELLE_ROLE = {
    PROFESSIONNEL: 'professionnel',
    AGENT_ADMINISTRATION: 'agent_administration',
    SUPERVISEUR: 'superviseur',
    MINISTRE: 'ministre',
    ADMIN_SYSTEME: 'admin_systeme',
};
export const LIBELLES_ROLE = valeurs(LIBELLE_ROLE);
/** Niveaux d'accès admis par `CHECK (niveau_acces BETWEEN 1 AND 5)`. */
export const NIVEAU_ACCES_MIN = 1;
export const NIVEAU_ACCES_MAX = 5;
/** Niveau d'accès attribué à chaque rôle par les seeds de la migration 001. */
export const NIVEAU_ACCES_PAR_ROLE = Object.freeze({
    [LIBELLE_ROLE.PROFESSIONNEL]: 1,
    [LIBELLE_ROLE.AGENT_ADMINISTRATION]: 3,
    [LIBELLE_ROLE.SUPERVISEUR]: 4,
    [LIBELLE_ROLE.MINISTRE]: 4,
    [LIBELLE_ROLE.ADMIN_SYSTEME]: 5,
});
/** `PERMISSION.action` — actions granulaires du RBAC. */
export const ACTION_PERMISSION = {
    LIRE: 'LIRE',
    CREER: 'CREER',
    MODIFIER: 'MODIFIER',
    SUPPRIMER: 'SUPPRIMER',
    VALIDER: 'VALIDER',
    SIGNER: 'SIGNER',
};
export const ACTIONS_PERMISSION = valeurs(ACTION_PERMISSION);
/** `SESSION.statut`. */
export const STATUT_SESSION = {
    ACTIVE: 'active',
    EXPIREE: 'expiree',
    REVOQUEE: 'revoquee',
};
export const STATUTS_SESSION = valeurs(STATUT_SESSION);
// -----------------------------------------------------------------------------
// Journalisation — tables INSERT ONLY
// -----------------------------------------------------------------------------
/** `JOURNAL_AUDIT.type_operation`. */
export const TYPE_OPERATION_AUDIT = {
    INSERT: 'INSERT',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
    LOGIN: 'LOGIN',
    LOGOUT: 'LOGOUT',
    VALIDER: 'VALIDER',
    REJETER: 'REJETER',
    SIGNER: 'SIGNER',
    IMPRIMER: 'IMPRIMER',
    AUTRE: 'AUTRE',
};
export const TYPES_OPERATION_AUDIT = valeurs(TYPE_OPERATION_AUDIT);
/** `JOURNAL_CONNEXION.resultat`. */
export const RESULTAT_CONNEXION = {
    SUCCES: 'succes',
    ECHEC_MDP: 'echec_mdp',
    VERROUILLE: 'verrouille',
    COMPTE_INACTIF: 'compte_inactif',
    AUTRE: 'autre',
};
export const RESULTATS_CONNEXION = valeurs(RESULTAT_CONNEXION);
/** `JOURNAL_WORKFLOW.action_effectuee`. */
export const ACTION_WORKFLOW = {
    SOUMIS: 'SOUMIS',
    VALIDE: 'VALIDE',
    REJETE: 'REJETE',
    TRANSMIS: 'TRANSMIS',
    SIGNE: 'SIGNE',
    ANNULE: 'ANNULE',
    AUTRE: 'AUTRE',
};
export const ACTIONS_WORKFLOW = valeurs(ACTION_WORKFLOW);
/** `ALERTE_SECURITE.type_alerte`. */
export const TYPE_ALERTE = {
    BRUTEFORCE: 'bruteforce',
    FALSIFICATION: 'falsification',
    ANOMALIE: 'anomalie',
    ANOMALIE_VERIFICATION: 'anomalie_verification',
    IMPRESSION_EXCESSIVE: 'impression_excessive',
    AUTRE: 'autre',
};
export const TYPES_ALERTE = valeurs(TYPE_ALERTE);
/** `ALERTE_SECURITE.statut`. */
export const STATUT_ALERTE = {
    OUVERTE: 'ouverte',
    EN_TRAITEMENT: 'en_traitement',
    RESOLUE: 'resolue',
};
export const STATUTS_ALERTE = valeurs(STATUT_ALERTE);
// -----------------------------------------------------------------------------
// Sécurité documentaire
// -----------------------------------------------------------------------------
/**
 * `JOURNAL_VERIFICATION.type_document` et `STOCK_VIGNETTES.table_document` —
 * les trois tables porteuses d'un document vérifiable par QR Code.
 */
export const TABLE_DOCUMENT_VERIFIABLE = {
    LICENCE: 'LICENCE',
    PERMIS: 'PERMIS',
    DOCUMENT_DELIVRE: 'DOCUMENT_DELIVRE',
};
export const TABLES_DOCUMENT_VERIFIABLE = valeurs(TABLE_DOCUMENT_VERIFIABLE);
/**
 * `JOURNAL_VERIFICATION.resultat` — verdict rendu à l'agent après un scan.
 * Correspond aux cinq états d'affichage de la spécification de sécurité §5.2.
 */
export const RESULTAT_VERIFICATION = {
    VALIDE: 'valide',
    EXPIRE: 'expire',
    REVOQUE: 'revoque',
    FALSIFIE: 'falsifie',
    INCONNU: 'inconnu',
};
export const RESULTATS_VERIFICATION = valeurs(RESULTAT_VERIFICATION);
/** `STOCK_VIGNETTES.statut`. */
export const STATUT_VIGNETTE = {
    DISPONIBLE: 'disponible',
    ASSOCIE: 'associe',
    INVALIDE: 'invalide',
};
export const STATUTS_VIGNETTE = valeurs(STATUT_VIGNETTE);
/**
 * Seuil d'alerte sur le compteur d'impressions d'un document officiel.
 * Spécification de sécurité §2.3 et DoD : « alerte au-delà de 3 ».
 */
export const SEUIL_ALERTE_IMPRESSIONS = 3;
// -----------------------------------------------------------------------------
// Notification in-app (domaine 6 de la v5.0)
// -----------------------------------------------------------------------------
/** `NOTIFICATION.type_notif`. */
export const TYPE_NOTIFICATION = {
    DEMANDE_SOUMISE: 'demande_soumise',
    ETAPE_VALIDEE: 'etape_validee',
    DOCUMENT_PRET: 'document_pret',
    ALERTE_SECURITE: 'alerte_securite',
    RAPPEL_EXPIRATION: 'rappel_expiration',
    /** Un avis de paiement est émis et attend règlement (migration 007). */
    PAIEMENT_ATTENDU: 'paiement_attendu',
    /** Un règlement est imputé, partiellement ou en totalité (migration 007). */
    PAIEMENT_RECU: 'paiement_recu',
    /** Un ticket d'incident attend ou porte une réponse (migration 008). */
    ASSISTANCE: 'assistance',
    AUTRE: 'autre',
};
export const TYPES_NOTIFICATION = valeurs(TYPE_NOTIFICATION);
//# sourceMappingURL=securite.js.map